import { useState, useEffect, useContext } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '../../../../2-component';
import { FETCH_CLIENTS } from '../../queries/queriesMutations';
import { SearchUsersData, SubTradeSelectedUser, SubTradeRequestSearchUser } from './subTradeRequestsTypes';
import { UserContext } from '../../../../../providers/userContextProvider';

interface ClientsAutocompleteProps {
  onChange: (user: SubTradeSelectedUser) => void
}

const ClientsAutocomplete = ({ onChange }: ClientsAutocompleteProps) => {
  const { t } = useTranslation(['rebalanceReportV2']);
  const [value, setValue] = useState<SubTradeSelectedUser | ''>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [debouncedValue, setDebouncedValue] = useState<string>('');
  const [clientList, setClientList] = useState<SubTradeRequestSearchUser[]>([]);
  const { activeOrganization, activeCurrency } = useContext(UserContext);

  const perPage = 10;
  const page = 1;

  const { loading, refetch } = useQuery(FETCH_CLIENTS, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        pagination: {
          perPage,
          page,
        },
        filter: {
          organizationId: activeOrganization.id,
          query: debouncedValue,
          types: ['INDIVIDUAL'],
        },
      },
      currency: activeCurrency || 'CAD',
    },
    onCompleted: (response: SearchUsersData) => {
      setClientList(response?.searchUsers?.users || []);
    },
  });

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, 300);

    return () => clearTimeout(timerId);
  }, [inputValue]);

  useEffect(() => {
    refetch({
      input: {
        pagination: {
          perPage,
          page,
        },
        filter: {
          organizationId: activeOrganization.id,
          query: debouncedValue,
          types: ['INDIVIDUAL'],
        },
      },
      currency: activeCurrency || 'CAD',
    });
  }, [debouncedValue, refetch, activeCurrency, activeOrganization.id]);

  const getOptionLabel = (option: any) => {
    if (option?.firstName && option?.lastName) {
      return `${option?.firstName} ${option?.lastName}`;
    }
    return option?.entityName || '';
  };

  return (
    <Autocomplete
      options={clientList}
      value={value}
      onChange={(event: any, newValue: SubTradeSelectedUser) => {
        setValue(newValue || '');
        onChange(newValue);
      }}
      onInputChange={(event: any, newInputValue: string) => {
        setInputValue(newInputValue);
      }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option: any, val: any) => option.id === val.id}
      label={t('columns.client')}
      loading={loading}
      fullWidth
    />
  );
};

export default ClientsAutocomplete;
